import React, { useState, useEffect, useCallback } from "react";
import {
  collection as fsCollection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "./button";
import { Heading } from "./heading";
import { Switch } from "./switch";
import { showAlert } from "../alertService";
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  ChevronLeftIcon,
  Bars3Icon,
  IdentificationIcon,
  HomeIcon,
  GlobeAltIcon,
} from "@heroicons/react/20/solid";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogActions,
} from "./dialog";

// Manager genérico para las colecciones de landings v2 (FRA-284):
// eventTypes y ciudades. Lista con orden drag & drop + toggle activo, y un
// editor por documento con campos de identidad + copy bilingüe de landing.
// Los subcomponentes viven a nivel de módulo (no inline) para que los inputs
// no pierdan foco por remount en cada render del padre.

// Mismo patrón de pills ES/EN que BilingualDescriptionField/BlogForm.
const PILL_GROUP = "inline-flex items-center gap-1 rounded-full bg-gray-100 p-1";
const PILL_IDLE =
  "rounded-full px-4 py-1.5 text-sm text-gray-600 transition hover:text-[#1b4d2e]";
const PILL_ACTIVE =
  "rounded-full bg-[#1b4d2e] px-4 py-1.5 text-sm font-semibold text-white shadow-sm";

const INPUT = "w-full border border-gray-300 p-2.5 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#1b4d2e] focus:border-[#1b4d2e]";
const FIELD_LABEL = "block text-sm font-medium text-gray-800";
const FIELD_HINT = "text-xs text-gray-500 mt-0.5 mb-1.5";

// Label + explicación de dónde aparece el campo. La ayuda va ARRIBA del
// input para que se lea antes de escribir.
function Field({ label, hint, children }) {
  return (
    <div>
      <label className={FIELD_LABEL}>{label}</label>
      {hint ? <p className={FIELD_HINT}>{hint}</p> : <div className="mb-1.5" />}
      {children}
    </div>
  );
}

const ACCENT = "#1b4d2e";

// Botón primario con el verde de la marca (mismo look que "Nuevo venue").
function AccentButton({ children, ...props }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
      style={{ backgroundColor: ACCENT }}
      {...props}
    >
      {children}
    </button>
  );
}

// Tarjeta de sección del editor: ícono + título, ayuda debajo y una acción
// opcional a la derecha (p. ej. las pills ES/EN).
function EditorCard({ icon: Icon, title, hint, action, children }) {
  return (
    <div className="mb-6 rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="mb-1 flex items-start justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: ACCENT + "1a" }}
          >
            <Icon className="h-4 w-4" style={{ color: ACCENT }} />
          </div>
          <Heading level={3}>{title}</Heading>
        </div>
        {action}
      </div>
      {hint && <p className="mt-1 mb-5 text-xs text-gray-500">{hint}</p>}
      {children}
    </div>
  );
}

// --- helpers de paths anidados ("label.ES" -> draft.label.ES) ---

function getPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function setPath(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

const EMPTY_LANDING = {
  h1: "",
  lead: "",
  metaTitle: "",
  metaDescription: "",
  intro: [],
  faqs: [],
};

function landingOf(draft, lang) {
  return { ...EMPTY_LANDING, ...((draft.landing || {})[lang] || {}) };
}

// --- editor del copy de landing para UN idioma ---

function LandingLangFields({ landing, onChange, hasComboH1 = false }) {
  const set = (key, value) => onChange({ ...landing, [key]: value });

  const setIntro = (idx, value) => {
    const intro = [...(landing.intro || [])];
    intro[idx] = value;
    set("intro", intro);
  };

  const setFaq = (idx, key, value) => {
    const faqs = (landing.faqs || []).map((f, i) =>
      i === idx ? { ...f, [key]: value } : f
    );
    set("faqs", faqs);
  };

  return (
    <div className="space-y-7">
      <Field
        label="H1 — título principal"
        hint="El encabezado grande que se ve al abrir la landing."
      >
        <input
          className={INPUT}
          value={landing.h1 || ""}
          onChange={(e) => set("h1", e.target.value)}
        />
      </Field>

      <Field
        label="Lead — bajada del título"
        hint="Frase corta debajo del H1, antes del buscador."
      >
        <textarea
          className={INPUT}
          rows={2}
          value={landing.lead || ""}
          onChange={(e) => set("lead", e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field
          label="Meta title — Google"
          hint="Título del resultado en Google y de la pestaña del navegador."
        >
          <input
            className={INPUT}
            value={landing.metaTitle || ""}
            onChange={(e) => set("metaTitle", e.target.value)}
          />
        </Field>
        <Field
          label="Meta description — Google"
          hint="Descripción del resultado en Google (~155 caracteres)."
        >
          <textarea
            className={INPUT}
            rows={2}
            value={landing.metaDescription || ""}
            onChange={(e) => set("metaDescription", e.target.value)}
          />
        </Field>
      </div>

      <div className="border-t border-gray-100 pt-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <span className={FIELD_LABEL}>Párrafos SEO</span>
            <p className={FIELD_HINT}>
              Texto largo que aparece DEBAJO del buscador de la landing. Es
              para Google y para quien quiere leer más — el usuario ve primero
              los venues.
            </p>
          </div>
          <Button
            color="zinc"
            onClick={() => set("intro", [...(landing.intro || []), ""])}
            className="px-3 py-1.5 text-xs flex-shrink-0"
          >
            <PlusIcon className="h-4 w-4" /> Agregar párrafo
          </Button>
        </div>
        <div className="space-y-3">
          {(landing.intro || []).map((p, idx) => (
            <div key={idx} className="flex gap-2">
              <textarea
                className={INPUT}
                rows={3}
                value={p}
                onChange={(e) => setIntro(idx, e.target.value)}
              />
              <button
                type="button"
                onClick={() =>
                  set(
                    "intro",
                    (landing.intro || []).filter((_, i) => i !== idx)
                  )
                }
                className="text-red-500 hover:text-red-700 self-start mt-2"
                title="Eliminar párrafo"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <span className={FIELD_LABEL}>Preguntas frecuentes</span>
            <p className={FIELD_HINT}>
              Acordeón al final de la landing. Las primeras también se mezclan
              en las landings combo (p. ej. /bodas/cdmx).
            </p>
          </div>
          <Button
            color="zinc"
            onClick={() =>
              set("faqs", [...(landing.faqs || []), { question: "", answer: "" }])
            }
            className="px-3 py-1.5 text-xs flex-shrink-0"
          >
            <PlusIcon className="h-4 w-4" /> Agregar FAQ
          </Button>
        </div>
        <div className="space-y-3">
          {(landing.faqs || []).map((f, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <input
                    className={INPUT}
                    placeholder="Pregunta"
                    value={f.question || ""}
                    onChange={(e) => setFaq(idx, "question", e.target.value)}
                  />
                  <textarea
                    className={INPUT}
                    rows={2}
                    placeholder="Respuesta"
                    value={f.answer || ""}
                    onChange={(e) => setFaq(idx, "answer", e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    set(
                      "faqs",
                      (landing.faqs || []).filter((_, i) => i !== idx)
                    )
                  }
                  className="text-red-500 hover:text-red-700 self-start"
                  title="Eliminar FAQ"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-6">
        <span className={FIELD_LABEL}>Landings combo (evento × ubicación)</span>
        <p className={`${FIELD_HINT} mb-4`}>
          Las páginas como /bodas/cdmx se arman solas mezclando piezas del
          evento y de la ubicación — estos campos son esas piezas.
        </p>
        <div className="space-y-5">
          {hasComboH1 && (
            <Field
              label="Template del H1 del combo"
              hint={'Usa {zona} como comodín — p. ej. "Venues y salones para bodas en {zona}".'}
            >
              <input
                className={INPUT}
                value={landing.comboH1Template || ""}
                onChange={(e) => set("comboH1Template", e.target.value)}
              />
            </Field>
          )}
          <Field
            label="Párrafo para combos"
            hint="Un párrafo propio que se inserta en la intro de cada landing combo."
          >
            <textarea
              className={INPUT}
              rows={3}
              value={landing.comboAngle || ""}
              onChange={(e) => set("comboAngle", e.target.value)}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

// --- editor de un documento completo ---

function DocEditor({ config, docId, initialDraft, onBack, onSaved }) {
  const [draft, setDraft] = useState(initialDraft);
  const [lang, setLang] = useState("ES");
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const setField = (path, value) => setDraft((d) => setPath(d, path, value));

  // El copy de landing es largo — no descartar un draft editado sin confirmar.
  const handleCancel = () => {
    if (JSON.stringify(draft) !== JSON.stringify(initialDraft)) {
      setConfirmDiscard(true);
    } else {
      onBack();
    }
  };

  const handleSave = async () => {
    const error = config.validateDraft(draft, docId);
    if (error) {
      showAlert(error, "error");
      return;
    }
    setSaving(true);
    try {
      const id = docId || config.docIdFor(draft);
      await setDoc(doc(db, config.collection, id), draft, { merge: true });
      showAlert("Guardado", "success");
      onSaved();
    } catch (e) {
      showAlert("Error al guardar: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={handleCancel}
            className="rounded-lg p-1.5 text-gray-500 transition hover:bg-zinc-100 hover:text-gray-800"
            title="Volver a la lista"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-400">
              {config.title}
            </p>
            <Heading level={2}>
              {docId
                ? config.rowLabel(draft)
                : config.nuevoLabel || `Nuevo ${config.singular}`}
            </Heading>
          </div>
        </div>
        <AccentButton onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </AccentButton>
      </div>

      {/* Identidad */}
      <EditorCard
        icon={IdentificationIcon}
        title="Datos básicos"
        hint="Nombre y URL — lo mínimo para que exista. El contenido de la página se edita más abajo."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* visibleIf(draft): campos condicionales — p. ej. los campos de
              guía (evento/ubicación/filtro/mapa) no aplican a un tema tipo
              "espacio" y se ocultan para que la separación sea clara. */}
          {config.identityFields
            .filter((f) => !f.visibleIf || f.visibleIf(draft))
            .map((f) => (
            <Field key={f.path} label={f.label} hint={f.hint}>
              {f.type === "checkbox" ? (
                <label className="flex items-center gap-2 cursor-pointer py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 accent-[#1b4d2e]"
                    checked={!!getPath(draft, f.path)}
                    onChange={(e) => setField(f.path, e.target.checked)}
                    disabled={f.lockedOnEdit && !!docId}
                  />
                  <span className="text-sm text-gray-700">
                    {f.checkboxLabel || "Sí"}
                  </span>
                </label>
              ) : f.type === "select" ? (
                <select
                  className={INPUT}
                  value={getPath(draft, f.path) || ""}
                  onChange={(e) => setField(f.path, e.target.value)}
                  disabled={f.lockedOnEdit && !!docId}
                >
                  <option value="">{f.emptyLabel || "— Seleccionar —"}</option>
                  {(f.options || []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={INPUT}
                  value={getPath(draft, f.path) || ""}
                  onChange={(e) => setField(f.path, e.target.value)}
                  placeholder={f.placeholder || ""}
                  disabled={f.lockedOnEdit && !!docId}
                />
              )}
              {f.warnOnEdit && docId && (
                <p className="mt-1 text-xs text-amber-600">{f.warnOnEdit}</p>
              )}
            </Field>
          ))}
        </div>
        {config.preview && config.preview(draft)}
      </EditorCard>

      {/* Descripción corta de homepage (opcional por colección) */}
      {config.hasHomeDesc === false ? null : (
      <EditorCard
        icon={HomeIcon}
        title="Descripción corta — homepage"
        hint="Una o dos líneas que acompañan su sección/tarjeta en la página principal del sitio. Si falta el inglés, se muestra el español."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Español">
            <textarea
              className={INPUT}
              rows={2}
              value={getPath(draft, "homeDesc.ES") || ""}
              onChange={(e) => setField("homeDesc.ES", e.target.value)}
            />
          </Field>
          <Field label="English">
            <textarea
              className={INPUT}
              rows={2}
              value={getPath(draft, "homeDesc.EN") || ""}
              onChange={(e) => setField("homeDesc.EN", e.target.value)}
            />
          </Field>
        </div>
      </EditorCard>
      )}

      {/* Copy de landing bilingüe (opcional por colección) */}
      {config.hasLandingCopy === false ? null : (
      <EditorCard
        icon={GlobeAltIcon}
        title="Contenido de la landing"
        hint={`Todo lo que se ve en la página pública (${lang === "ES" ? "versión en español" : "English version — cae al español si un campo está vacío"}).`}
        action={
          <div className={PILL_GROUP}>
            {["ES", "EN"].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={lang === l ? PILL_ACTIVE : PILL_IDLE}
              >
                {l === "ES" ? "Español" : "English"}
              </button>
            ))}
          </div>
        }
      >
        <LandingLangFields
          landing={landingOf(draft, lang)}
          onChange={(landing) => setField(`landing.${lang}`, landing)}
          hasComboH1={!!config.hasComboH1}
        />
      </EditorCard>
      )}

      {/* Guardar también al final: el copy de landing es largo y obligar a
          subir hasta arriba invita a cerrar sin guardar. */}
      <div className="flex justify-end gap-2 pb-10">
        <Button color="zinc" onClick={handleCancel}>
          Cancelar
        </Button>
        <AccentButton onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </AccentButton>
      </div>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)}>
        <DialogTitle>Cambios sin guardar</DialogTitle>
        <DialogDescription>
          Tienes cambios sin guardar. Si sales ahora se perderán.
        </DialogDescription>
        <DialogActions>
          <Button color="zinc" onClick={() => setConfirmDiscard(false)}>
            Seguir editando
          </Button>
          <Button color="red" onClick={onBack}>
            Descartar cambios
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// --- lista + orquestación ---

export default function LandingsManagerBase({ config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {docId, draft} | null
  const [deleting, setDeleting] = useState(null); // item | null
  // {docId: n} — cuántos venues públicos mostraría la landing de cada doc.
  const [venueCounts, setVenueCounts] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(fsCollection(db, config.collection));
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      setItems(docs);
      if (config.loadCounts) {
        // El conteo es informativo — si falla no bloquea el manager.
        config
          .loadCounts(docs)
          .then(setVenueCounts)
          .catch((e) => console.error("Error al contar venues:", e));
      }
    } catch (e) {
      showAlert("Error al cargar: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActivo = async (item) => {
    try {
      await updateDoc(doc(db, config.collection, item.id), {
        activo: !item.activo,
      });
      setItems((arr) =>
        arr.map((i) => (i.id === item.id ? { ...i, activo: !i.activo } : i))
      );
    } catch (e) {
      showAlert("Error al actualizar: " + e.message, "error");
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const arr = Array.from(items);
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setItems(arr);
    try {
      const batch = writeBatch(db);
      arr.forEach((item, i) =>
        batch.update(doc(db, config.collection, item.id), { orden: i + 1 })
      );
      await batch.commit();
    } catch (e) {
      showAlert("Error al reordenar: " + e.message, "error");
      load();
    }
  };

  const handleDelete = async () => {
    const item = deleting;
    setDeleting(null);
    if (config.canDelete) {
      const veto = await config.canDelete(item);
      if (veto) {
        showAlert(veto, "error");
        return;
      }
    }
    try {
      await deleteDoc(doc(db, config.collection, item.id));
      setItems((arr) => arr.filter((i) => i.id !== item.id));
      showAlert("Eliminado", "success");
    } catch (e) {
      showAlert("Error al eliminar: " + e.message, "error");
    }
  };

  if (editing) {
    return (
      <DocEditor
        config={config}
        docId={editing.docId}
        initialDraft={editing.draft}
        onBack={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  const activeCount = items.filter((i) => i.activo).length;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <div className="flex items-center gap-3">
          <Heading level={2}>{config.title}</Heading>
          {!loading && items.length > 0 && (
            <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-600">
              {activeCount} activa{activeCount === 1 ? "" : "s"} ·{" "}
              {items.length - activeCount} oculta
              {items.length - activeCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <AccentButton
          onClick={() => setEditing({ docId: null, draft: config.newDraft() })}
        >
          <PlusIcon className="h-4 w-4" />
          Agregar {config.singular}
        </AccentButton>
      </div>
      <p className="text-sm text-gray-500 mb-6">{config.subtitle}</p>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-zinc-100" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-zinc-200 px-6 py-12 text-center">
          <p className="text-sm text-zinc-500 mb-4">
            Nada por aquí todavía.
          </p>
          <AccentButton
            onClick={() => setEditing({ docId: null, draft: config.newDraft() })}
          >
            <PlusIcon className="h-4 w-4" />
            Crear {config.singular}
          </AccentButton>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={config.collection}>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-2"
              >
                {/* rowGroup/rowGroups (opcional): agrupa la lista con títulos
                    de sección. El display se ordena por grupo (estable); solo
                    usarlo en colecciones donde el orden no es significativo
                    en el sitio (p. ej. temas, que se barajan al azar). */}
                {(config.rowGroup
                  ? [...items].sort(
                      (a, b) =>
                        (config.rowGroups || []).indexOf(config.rowGroup(a)) -
                        (config.rowGroups || []).indexOf(config.rowGroup(b))
                    )
                  : items
                ).map((item, index, arr) => (
                  <React.Fragment key={item.id}>
                    {config.rowGroup &&
                      (index === 0 ||
                        config.rowGroup(arr[index - 1]) !==
                          config.rowGroup(item)) && (
                        <p
                          className={`px-1 pb-1 text-xs font-bold uppercase tracking-wider text-zinc-400 ${
                            index === 0 ? "" : "pt-4"
                          }`}
                        >
                          {config.rowGroup(item)}
                        </p>
                      )}
                  <Draggable draggableId={item.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`flex items-center gap-3 rounded-xl border bg-white p-3 shadow-sm transition ${
                          dragSnapshot.isDragging
                            ? "border-[#1b4d2e] shadow-lg ring-1 ring-[#1b4d2e]/30"
                            : "border-gray-200 hover:border-zinc-300 hover:shadow"
                        }`}
                      >
                        <div
                          {...dragProvided.dragHandleProps}
                          className="flex items-center self-stretch px-1 text-zinc-300 hover:text-zinc-500 cursor-grab active:cursor-grabbing"
                          title="Arrastra para reordenar"
                        >
                          <Bars3Icon className="h-5 w-5" />
                        </div>
                        {config.rowMedia && config.rowMedia(item)}
                        <button
                          type="button"
                          className={`min-w-0 flex-1 py-0.5 text-left ${
                            item.activo ? "" : "opacity-50"
                          }`}
                          onClick={() =>
                            setEditing({
                              docId: item.id,
                              draft: (({ id, ...rest }) => rest)(item),
                            })
                          }
                        >
                          <p className="font-medium text-gray-900 truncate">
                            {config.rowLabel(item)}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {config.rowDetail(item)}
                          </p>
                        </button>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {/* Cuántos venues públicos mostraría esta landing hoy.
                              0 en ámbar: landing viva pero vacía (candidata a
                              apagarse con el toggle). */}
                          {venueCounts && venueCounts[item.id] != null && (
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                venueCounts[item.id] === 0
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-zinc-100 text-zinc-600"
                              }`}
                              title={
                                venueCounts[item.id] === 0
                                  ? "Ningún venue público cae en esta landing — se vería vacía"
                                  : `${venueCounts[item.id]} venue${venueCounts[item.id] === 1 ? "" : "s"} públicos en esta landing`
                              }
                            >
                              {venueCounts[item.id]} venue
                              {venueCounts[item.id] === 1 ? "" : "s"}
                            </span>
                          )}
                          {/* Toggle activo: única condición de visibilidad de la
                              landing (sin umbral de inventario) */}
                          <label
                            className="flex items-center gap-2 cursor-pointer"
                            title={
                              item.activo
                                ? "Visible en el sitio — apaga para ocultar"
                                : "Oculta en el sitio — prende para publicar"
                            }
                          >
                            <span
                              className={`text-xs font-medium ${
                                item.activo ? "text-green-800" : "text-gray-500"
                              }`}
                            >
                              {item.activo ? "Activa" : "Inactiva"}
                            </span>
                            <Switch
                              color="accent"
                              checked={!!item.activo}
                              onChange={() => toggleActivo(item)}
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() =>
                              setEditing({
                                docId: item.id,
                                draft: (({ id, ...rest }) => rest)(item),
                              })
                            }
                            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-zinc-100 hover:text-[#1b4d2e]"
                            title="Editar"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleting(item)}
                            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                            title="Eliminar"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                  </React.Fragment>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Sección extra bajo la lista (p. ej. la matriz de combos) — solo en
          modo lista, nunca sobre el editor. */}
      {!loading && config.belowList}

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogDescription>
          {deleting &&
            (config.deleteDescription
              ? config.deleteDescription(deleting)
              : `¿Eliminar "${config.rowLabel(deleting)}"? Su landing dejará de existir (404). Si solo quieres ocultarla temporalmente, usa el toggle Activa/Inactiva.`)}
        </DialogDescription>
        <DialogActions>
          <Button color="zinc" onClick={() => setDeleting(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
